import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Box,
    Typography,
    Switch,
    FormControlLabel,
    Divider,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { expenseService } from "../../services/expenseService";
import {
    ExpenseCategory,
    ExpenseCategoryCreate,
    ExpenseCategoryUpdate,
} from "../../types";

interface ExpenseCategoryManagerProps {
    open: boolean;
    onClose: () => void;
}

export const ExpenseCategoryManager: React.FC<ExpenseCategoryManagerProps> = ({
    open,
    onClose,
}) => {
    const [categories, setCategories] = useState<ExpenseCategory[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [editingCategory, setEditingCategory] =
        useState<ExpenseCategory | null>(null);
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        is_active: true,
    });

    useEffect(() => {
        if (open) {
            fetchCategories();
        }
    }, [open]);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await expenseService.getCategories(0, 500, false);
            setCategories(response.items);
        } catch (err: any) {
            console.error("Failed to fetch categories:", err);
            setError("Failed to load categories");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        if (!formData.name.trim()) {
            setError("Category name is required");
            return;
        }

        try {
            setLoading(true);

            if (editingCategory) {
                await expenseService.updateCategory(
                    editingCategory.category_id,
                    formData as ExpenseCategoryUpdate,
                );
            } else {
                await expenseService.createCategory(
                    formData as ExpenseCategoryCreate,
                );
            }

            setFormData({ name: "", description: "", is_active: true });
            setEditingCategory(null);
            fetchCategories();
        } catch (err: any) {
            console.error("Failed to save category:", err);
            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Failed to save category",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (category: ExpenseCategory) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            description: category.description || "",
            is_active: category.is_active,
        });
    };

    const handleDelete = async (categoryId: string) => {
        if (!window.confirm("Are you sure you want to delete this category?")) {
            return;
        }

        try {
            setLoading(true);
            await expenseService.deleteCategory(categoryId);
            fetchCategories();
        } catch (err: any) {
            console.error("Failed to delete category:", err);
            setError(
                err.response?.data?.detail ||
                    err.message ||
                    "Failed to delete category. It may be in use.",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({ name: "", description: "", is_active: true });
        setEditingCategory(null);
        setError("");
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Manage Expense Categories</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError("")}
                    >
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {editingCategory ? "Edit Category" : "Add New Category"}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Category Name"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    name: e.target.value,
                                })
                            }
                            required
                            fullWidth
                            size="small"
                        />
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    description: e.target.value,
                                })
                            }
                            multiline
                            rows={2}
                            fullWidth
                            size="small"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            is_active: e.target.checked,
                                        })
                                    }
                                />
                            }
                            label="Active"
                        />
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={editingCategory ? <Edit /> : <Add />}
                            >
                                {editingCategory ? "Update" : "Add"}
                            </Button>
                            {editingCategory && (
                                <Button
                                    onClick={handleCancel}
                                    disabled={loading}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Existing Categories
                </Typography>
                <List>
                    {categories.map((category) => (
                        <ListItem
                            key={category.category_id}
                            secondaryAction={
                                <Box>
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleEdit(category)}
                                        disabled={loading}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        edge="end"
                                        onClick={() =>
                                            handleDelete(category.category_id)
                                        }
                                        disabled={loading}
                                        color="error"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Box>
                            }
                        >
                            <ListItemText
                                primary={category.name}
                                secondary={
                                    <>
                                        {category.description && (
                                            <span>
                                                {category.description} •{" "}
                                            </span>
                                        )}
                                        <span
                                            style={{
                                                color: category.is_active
                                                    ? "green"
                                                    : "gray",
                                            }}
                                        >
                                            {category.is_active
                                                ? "Active"
                                                : "Inactive"}
                                        </span>
                                    </>
                                }
                            />
                        </ListItem>
                    ))}
                    {categories.length === 0 && !loading && (
                        <ListItem>
                            <ListItemText
                                primary="No categories yet"
                                secondary="Add your first category above"
                            />
                        </ListItem>
                    )}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};
